import {
  ELIG_TOKENS,
  READINESS_FILLED,
  READINESS_TONE,
} from '@features/eligibility-checks/theme/readinessTokens';
import type { BlockReadiness } from '@features/eligibility-checks/types/readiness';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import {
  useAppointmentOverrides,
  useMergedAppointment,
} from '../../context/AppointmentOverridesContext';
import { useEligibilityDrawer } from '../../context/EligibilityDrawerContext';
import { type ExperimentalAppointment, getFixtureSnapshot } from '../../fixtures';

import { AppointmentPropertiesTable } from './AppointmentPropertiesTable';
import { SendInvoiceButton } from './SendInvoiceButton';
import type { NirvanaPolicy } from './snapshotShape';
import { WaitlistCandidatesPanel } from './WaitlistCandidatesPanel';

const dollars = (cents: number | null | undefined): string => {
  if (cents == null) return '—';
  if (cents % 100 === 0) return `$${cents / 100}`;
  return `$${(cents / 100).toFixed(2)}`;
};

const percent = (used: number, total: number): number => {
  if (!total) return 0;
  return Math.max(0, Math.min(100, (used / total) * 100));
};

// Footer CTA logic per handoff §4.5, adapted to the API contract:
//   verified              → no primary CTA (nothing to do today)
//   collect / collected   → "Mark verified" primary
//   attention / blocked / info → primary uses readiness_label (the chip's verb)
const getPrimaryCta = (readiness: BlockReadiness, label: string | null): string | null => {
  if (readiness === 'verified') return null;
  if (readiness === 'collect' || readiness === 'collected') return 'Mark verified';
  return label;
};

export const EligibilityDrawer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { appointment, closeDrawer } = useEligibilityDrawer();

  return (
    <Drawer
      anchor="right"
      open={appointment != null}
      onClose={closeDrawer}
      PaperProps={{
        sx: {
          width: isMobile ? '100vw' : 480,
          bgcolor: ELIG_TOKENS.bg,
          color: ELIG_TOKENS.ink,
        },
      }}
    >
      {appointment && (
        <DrawerBody
          appointment={appointment}
          onClose={closeDrawer}
          isMobile={isMobile}
        />
      )}
    </Drawer>
  );
};

// Body is rendered only when there's an appointment, so it can use the
// override-aware merged read without nullable plumbing.
const DrawerBody = ({
  appointment,
  onClose,
  isMobile,
}: {
  appointment: ExperimentalAppointment;
  onClose: () => void;
  isMobile: boolean;
}) => {
  const merged = useMergedAppointment(appointment);
  const { setOverride } = useAppointmentOverrides();

  const readiness = merged.block_state?.readiness ?? null;
  const readinessLabel = merged.block_state?.readiness_label ?? null;
  const inputs = merged.block_state?.readiness_inputs;
  const snapshot = getFixtureSnapshot(merged.eligibility_snapshot_id) as NirvanaPolicy | null;

  const tone = readiness ? READINESS_TONE[readiness] : 'gray';
  const filled = readiness ? READINESS_FILLED[readiness] : false;
  const causeColor = ELIG_TOKENS[tone];
  const checkedAt = inputs?.eligibility?.last_checked_at;
  const provenance = checkedAt
    ? `From Nirvana · checked ${formatDistanceToNow(new Date(checkedAt), { addSuffix: true })}`
    : 'From Nirvana';
  const primaryCta = readiness && readinessLabel ? getPrimaryCta(readiness, readinessLabel) : null;

  // Slot-open detection — drives whether the waitlist candidates panel
  // surfaces. Cancelled and no-show are post-fact (slot already free);
  // rescheduling is pre-fact (slot might free up).
  const reality = merged.block_state?.reality;
  const visitStatus = merged.visit_status ?? null;
  const slotOpenReason: 'cancelled' | 'no_show' | 'rescheduling' | null =
    reality === 'cancelled'
      ? 'cancelled'
      : visitStatus === 'no_show'
        ? 'no_show'
        : reality === 'rescheduling'
          ? 'rescheduling'
          : null;

  // Cancel-from-drawer demo: flip block_state.reality via the override so
  // the appointment immediately reads as cancelled and the waitlist panel
  // appears below. Production wires this to the real cancel mutation.
  const isCancelled = reality === 'cancelled';
  const handleCancel = () => {
    setOverride(merged.appointment_id, { reality: 'cancelled' });
  };
  const handleUncancel = () => {
    setOverride(merged.appointment_id, { reality: 'real' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DrawerHeader appointment={merged} onClose={onClose} />

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        {readiness && readinessLabel && (
          <DrawerHero
            readiness={readiness}
            label={readinessLabel}
            summary={inputs?.eligibility?.summary ?? ''}
            color={causeColor}
            filled={filled}
            action={
              readiness === 'collect' ? (
                <SendInvoiceButton appointment={merged} source="eligibility_drawer" />
              ) : null
            }
          />
        )}

        <DrawerSection title="Properties">
          <AppointmentPropertiesTable appointment={merged} />
        </DrawerSection>

        {slotOpenReason && (
          <DrawerSection title="Waitlist matches">
            <WaitlistCandidatesPanel appointment={merged} reason={slotOpenReason} />
          </DrawerSection>
        )}

        {snapshot && <CostSection snapshot={snapshot} provenance={provenance} />}

        {snapshot && <PlanSection snapshot={snapshot} provenance={provenance} />}
      </Box>

      <DrawerFooter
        primaryCta={primaryCta}
        primaryColor={causeColor}
        isMobile={isMobile}
        isCancelled={isCancelled}
        onCancel={handleCancel}
        onUncancel={handleUncancel}
      />
    </Box>
  );
};

// ─── Subcomponents ─────────────────────────────────────────────────────────

const DrawerHeader = ({
  appointment,
  onClose,
}: {
  appointment: NonNullable<ReturnType<typeof useEligibilityDrawer>['appointment']>;
  onClose: () => void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${ELIG_TOKENS.line}`,
      }}
    >
      <Typography variant="h6" sx={{ color: ELIG_TOKENS.midnight, lineHeight: 1.2 }}>
        {appointment.title}
      </Typography>
      <IconButton onClick={onClose} size="small" aria-label="Close drawer">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const DrawerHero = ({
  readiness,
  label,
  summary,
  color,
  filled,
  action,
}: {
  readiness: BlockReadiness;
  label: string;
  summary: string;
  color: string;
  filled: boolean;
  action?: React.ReactNode;
}) => {
  const isVerified = readiness === 'verified';
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            flexShrink: 0,
            ...(filled
              ? { bgcolor: color }
              : { border: `2px solid ${color}`, bgcolor: 'transparent' }),
          }}
        />
        <Typography
          variant="h5"
          sx={{
            color,
            fontWeight: isVerified ? 500 : 600,
            ...(isVerified
              ? {}
              : { textDecoration: 'underline', textDecorationThickness: '2px' }),
          }}
        >
          {label}
        </Typography>
      </Box>
      {summary && (
        <Typography variant="body1" sx={{ color: ELIG_TOKENS.ink, lineHeight: 1.5 }}>
          {summary}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
  );
};

const DrawerSection = ({
  title,
  provenance,
  children,
}: {
  title: string;
  provenance?: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        mb: 1,
      }}
    >
      <Typography variant="overline" sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600 }}>
        {title}
      </Typography>
      {provenance && (
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
          {provenance}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

const DrawerField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const DrawerMeter = ({
  label,
  used,
  total,
}: {
  label: string;
  used: number;
  total: number;
}) => {
  const value = percent(used, total);
  return (
    <Box sx={{ py: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: ELIG_TOKENS.ink, fontWeight: 500 }}>
          {dollars(used)} of {dollars(total)} met
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: ELIG_TOKENS.line,
          '& .MuiLinearProgress-bar': { bgcolor: ELIG_TOKENS.midnight },
        }}
      />
    </Box>
  );
};

const CostSection = ({
  snapshot,
  provenance,
}: {
  snapshot: NirvanaPolicy;
  provenance: string;
}) => {
  const due = snapshot.member_obligation;
  const ded = snapshot.deductible;
  const dedRem = snapshot.remaining_deductible;
  const oop = snapshot.oop_max;
  const oopRem = snapshot.remaining_oop_max;
  const postDed = snapshot.post_deductible_member_obligation;
  const sessionsLeft = snapshot.remaining_sessions_before_deductible;

  return (
    <DrawerSection title="Cost" provenance={provenance}>
      <Typography variant="h4" sx={{ color: ELIG_TOKENS.midnight, fontWeight: 600, mb: 0.5 }}>
        {dollars(due)} today
      </Typography>
      {postDed != null && postDed !== due && (
        <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft, mb: 1.5 }}>
          {dollars(postDed)} after deductible met
          {sessionsLeft != null ? ` · ~${sessionsLeft} sessions` : ''}
        </Typography>
      )}
      {ded != null && dedRem != null && ded > 0 && (
        <DrawerMeter label="Deductible" used={ded - dedRem} total={ded} />
      )}
      {oop != null && oopRem != null && oop > 0 && (
        <DrawerMeter label="Out-of-pocket max" used={oop - oopRem} total={oop} />
      )}
      <Box sx={{ mt: 1 }}>
        {snapshot.copayment != null && (
          <DrawerField label="Copay" value={dollars(snapshot.copayment)} />
        )}
        {snapshot.coinsurance != null && (
          <DrawerField label="Coinsurance" value={`${snapshot.coinsurance}%`} />
        )}
      </Box>
    </DrawerSection>
  );
};

const PlanSection = ({
  snapshot,
  provenance,
}: {
  snapshot: NirvanaPolicy;
  provenance: string;
}) => (
  <DrawerSection title="Plan" provenance={provenance}>
    {snapshot.plan_name && <DrawerField label="Plan" value={snapshot.plan_name} />}
    {snapshot.plan_type && snapshot.plan_type !== 'UNKNOWN' && (
      <DrawerField label="Type" value={snapshot.plan_type} />
    )}
    {snapshot.insurance_type && (
      <DrawerField label="Insurance" value={snapshot.insurance_type} />
    )}
    {snapshot.group_id && <DrawerField label="Group" value={snapshot.group_id} />}
    {snapshot.payer_id && <DrawerField label="Payer ID" value={snapshot.payer_id} />}
    {snapshot.relationship_to_subscriber && (
      <DrawerField label="Relationship" value={snapshot.relationship_to_subscriber} />
    )}
  </DrawerSection>
);

const DrawerFooter = ({
  primaryCta,
  primaryColor,
  isMobile,
  isCancelled,
  onCancel,
  onUncancel,
}: {
  primaryCta: string | null;
  primaryColor: string;
  isMobile: boolean;
  isCancelled: boolean;
  onCancel: () => void;
  onUncancel: () => void;
}) => (
  <Box
    sx={{
      borderTop: `1px solid ${ELIG_TOKENS.line}`,
      bgcolor: ELIG_TOKENS.card,
      px: 3,
      py: 2,
      display: 'flex',
      gap: 1,
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
    }}
  >
    {isCancelled ? (
      <Button
        variant="text"
        onClick={onUncancel}
        sx={{ color: ELIG_TOKENS.inkSoft, mr: 'auto' }}
      >
        Restore
      </Button>
    ) : (
      <Button
        variant="text"
        onClick={onCancel}
        sx={{ color: ELIG_TOKENS.red, mr: 'auto' }}
      >
        Cancel appointment
      </Button>
    )}
    <Button variant="text" sx={{ color: ELIG_TOKENS.inkSoft }}>
      Re-run
    </Button>
    <Button variant="text" sx={{ color: ELIG_TOKENS.inkSoft }}>
      Override
    </Button>
    {primaryCta && (
      <Button
        variant="contained"
        sx={{
          bgcolor: primaryColor,
          color: '#fff',
          '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
        }}
      >
        {primaryCta}
      </Button>
    )}
  </Box>
);
